import React from "react";
import { intl } from "LocaleWrapper";
import { Card, Table } from "@cofano/cck";
import { Box, Grid2 as Grid, Typography } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { ContractHours } from "view/ships/ShipEvents";
import { useFrontendTable } from "components/table";
import { ShipEventsEventReportsForPeriodFragment } from "__generated__/view/ships/graphql/ship.fragments.generated";

type Reports = ShipEventsEventReportsForPeriodFragment["reports"];
type Report = Reports[number];

const ShipEventsSummary = ({ reports, contractHours }: { reports: Reports, contractHours: ContractHours }) => {
	if (reports.length === 1) {
		return (
			<Grid container spacing={3}>
				{[ {
					title: intl.formatMessage({ id: "SHIP_EVENT_SAILING_HOURS" }),
					hours: reports[0].summary.sailingHours,
				}, {
					title: intl.formatMessage({ id: "SHIP_EVENT_WAITING_HOURS" }),
					hours: reports[0].summary.waitingHours,
				}, {
					title: intl.formatMessage({ id: "SHIP_EVENT_UN_LOAD_HOURS" }),
					hours: reports[0].summary.loadDischargeHours,
				}, {
					title: intl.formatMessage({ id: "SHIP_EVENT_TOTAL_HOURS" }),
					hours: reports[0].summary.totalHours,
					calculateDifference: true,
				}, {
					title: intl.formatMessage({ id: "SHIP_EVENT_REST_HOURS" }),
					hours: reports[0].summary.restHours,
				} ].map((item, _index, self) => {

					const hours = contractHours.find(it => it.ship.id === reports[0].ship.id)?.hours;

					return (
						<Grid size={{ xs: 18 / self.length, sm: 12 / self.length }} key={item.title}>
							<Card>
								<div className="summary-content">
									<Typography
										variant="h5"
										fontWeight="medium"
										fontSize="11px"
										color="text.primary">
										{item.title}
									</Typography>

									<Box sx={{ alignItems: "center", display: "flex", justifyContent: "space-between" }}>
										<Typography
											variant="caption"
											fontWeight="bold"
											fontSize="18px"
											color="text.primary">
											{item.hours.toFixed(1)}
										</Typography>

										{item.calculateDifference && hours && (Number(hours) > item.hours) &&  (
											<Typography
												variant="caption"
												fontWeight="bold"
												fontSize="13px"
												color="error"
												sx={{ alignItems: "center", display: "flex" }}>
												<ArrowDownwardIcon fontSize="small" style={{ marginRight: 4 }} />
												{(Number(hours) - item.hours).toFixed(1)}
											</Typography>
										)}
									</Box>
								</div>
							</Card>
						</Grid>
					);
				})}
			</Grid>
		);
	} else if (reports.length > 1) {
		return (
			<EventsSummaryTable reports={reports} contractHours={contractHours} />
		);
	} else {
		return <React.Fragment />;
	}
};

const EventsSummaryTable = ({ reports, contractHours }: { reports: Reports, contractHours: ContractHours }) => {
	const { tableProps } = useFrontendTable({
		id: "shipEventsSummary",
		content: reports.map(report => ({
			...report,
			id: report.ship.id,
		})),
		defaultPageSize: 10,
		saveForOrganizationEnabled: false,
	});

	return (
		<Card disablePadding>
			<Table
				{...tableProps}
				columns={[ {
					id: "ship.name",
					title: intl.formatMessage({ id: "SHIP" }),
				}, {
					id: "summary.sailingHours",
					title: intl.formatMessage({ id: "SHIP_EVENT_SAILING_HOURS" }),
					render: (row: Report) => row.summary.sailingHours.toFixed(1),
				}, {
					id: "summary.waitingHours",
					title: intl.formatMessage({ id: "SHIP_EVENT_WAITING_HOURS" }),
					render: (row: Report) => row.summary.waitingHours.toFixed(1),
				}, {
					id: "summary.loadDischargeHours",
					title: intl.formatMessage({ id: "SHIP_EVENT_UN_LOAD_HOURS" }),
					render: (row: Report) => row.summary.loadDischargeHours.toFixed(1),
				}, {
					id: "summary.totalHours",
					title: intl.formatMessage({ id: "SHIP_EVENT_TOTAL_HOURS" }),
					render: (row: Report) => {

						const hours = contractHours.find(it => it.ship.id === row.ship.id)?.hours;

						return (
							<Box sx={{ alignItems: "center", display: "flex" }}>
								<span>
									{row.summary.totalHours.toFixed(1)}
								</span>

								{hours && (Number(hours) > row.summary.totalHours) && (
									<Box
										ml={1}
										sx={{
											alignItems: "center",
											color: (theme) => theme.palette.error.main,
											display: "flex",
											fontSize: "11px",
											fontWeight: 600,
										}}>
										<ArrowDownwardIcon fontSize="small" style={{ fontSize: "14px", marginRight: 4 }} />
										{(Number(hours) - row.summary.totalHours).toFixed(1)}
									</Box>
								)}
							</Box>
						);
					},
				}, {
					id: "summary.restHours",
					title: intl.formatMessage({ id: "SHIP_EVENT_REST_HOURS" }),
					render: (row: Report) => row.summary.restHours.toFixed(1),
				} ]}
				disableFooter={reports.length <= 10}
			/>
		</Card>
	);
};

export default ShipEventsSummary;
